/**
 * Web Push subscription helper. Talks to /api/notifications.
 *
 * Browser story:
 *   1. Register service worker at /app/sw.js (scope /app/).
 *   2. Fetch the VAPID public key from the API.
 *   3. Notification.requestPermission() — user clicks Allow.
 *   4. PushManager.subscribe with the VAPID key.
 *   5. POST the subscription up so we can fan out via web-push later.
 */

import { api } from './api';

export interface NotificationPrefs {
  enabled: boolean;
  radiusMiles: number;
  categories: string[];
  location: { lat: number; lng: number } | null;
  digestEmail: boolean;
  quietHours: { start: number; end: number } | null;
  subscriptions: Array<{ id: number; endpoint: string; user_agent: string | null; created_at: number }>;
}

const supportsPush = () => typeof window !== 'undefined'
  && 'serviceWorker' in navigator
  && 'PushManager' in window
  && 'Notification' in window;

function urlBase64ToUint8Array(b64: string): Uint8Array {
  const padding = '='.repeat((4 - b64.length % 4) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

async function ensureRegistration(): Promise<ServiceWorkerRegistration> {
  // Service worker is served by Vite's build at /app/sw.js. Scope = /app/.
  const reg = await navigator.serviceWorker.register('/app/sw.js', { scope: '/app/' });
  await navigator.serviceWorker.ready;
  return reg;
}

export const notificationsApi = {
  supported: supportsPush,
  permission: () => (typeof Notification !== 'undefined' ? Notification.permission : 'denied'),

  vapidKey: () => api<{ key: string | null; ready: boolean }>('/api/notifications/vapid-public-key'),

  prefs: () => api<NotificationPrefs>('/api/notifications/preferences', { requireAuth: true }),

  setPrefs: (prefs: Partial<NotificationPrefs>) =>
    api<{ ok: true }>('/api/notifications/preferences', { method: 'POST', json: prefs, requireAuth: true }),

  /**
   * End-to-end subscribe: request permission, subscribe to PushManager, and
   * POST the subscription to the API. Returns the registration so callers
   * can chain a setPrefs call.
   */
  async subscribe(): Promise<{ ok: true; endpoint: string }> {
    if (!supportsPush()) throw new Error('Push not supported on this browser.');
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') throw new Error('Notifications denied.');

    const reg = await ensureRegistration();
    const k = await this.vapidKey();
    if (!k.key || !k.ready) throw new Error('Server push not configured.');

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(k.key),
      });
    }
    await api('/api/notifications/subscribe', {
      method: 'POST', json: { subscription: sub.toJSON() }, requireAuth: true,
    });
    return { ok: true, endpoint: sub.endpoint };
  },

  async unsubscribe(): Promise<void> {
    if (!supportsPush()) return;
    const reg = await ensureRegistration();
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    try { await api('/api/notifications/subscribe', { method: 'DELETE', json: { endpoint: sub.endpoint }, requireAuth: true }); } catch {}
    await sub.unsubscribe();
  },

  test: () => api<{ ok: true; sent: number; skipped: number; dead: number }>(
    '/api/notifications/test', { method: 'POST', requireAuth: true },
  ),

  /**
   * Listen for notification-click messages from the service worker and route
   * the user appropriately. Returns an unsubscribe function.
   */
  onNotificationClick(handler: (payload: any) => void): () => void {
    const onMsg = (e: MessageEvent) => {
      if (e.data?.type === 'open-from-notification') handler(e.data.payload);
    };
    navigator.serviceWorker?.addEventListener('message', onMsg);
    return () => navigator.serviceWorker?.removeEventListener('message', onMsg);
  },
};
